import {
  GetAccounts,
  ManagerP2Response,
  UserRole,
  getAccounts,
  getManagerParticipants,
} from '../../../../../../services/api';
import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Cache } from 'aws-amplify/utils';
import { CacheQuery } from '../../../../../auth/app/shared';
import { Preloader } from '../components';
import { useAppContext } from '../../../contexts';
import { useQueryParams } from '../shared';

const initialState: {
  readonly accounts: ReadonlyArray<GetAccounts>;
  readonly currentAccount?: ManagerP2Response[number];
} = {
  accounts: [],
};

const Context = createContext(initialState);

const { Provider } = Context;

const AccountsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { parsed } = useQueryParams();
  const { user } = useAppContext();
  const params = useParams();

  const [accounts, setAccounts] = useState(initialState.accounts);
  const [loading, setLoading] = useState(true);
  const [currentAccount, setCurrentAccount] = useState<ManagerP2Response[number]>();

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await getAccounts({ email: parsed.email });

      data && setAccounts(data);
    } catch (error) {
      // Best-effort: the account list is only used for the account-level report and the portfolio-name
      // label. In a Client Office session getAccounts is VPN-gated (403), so this must NOT bounce to
      // login and must NOT block rendering — the report itself loads from getReport, which is
      // session-aware (object-level access enforced on the backend).
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [parsed.email]);

  const fetchAccount = useCallback(async () => {
    if (accounts && !currentAccount && (user || (await Cache.getItem(CacheQuery.USER))?.email)) {
      const { data } = await getManagerParticipants({
        role: UserRole.manager,
        roleLoginEmail: user?.email ?? (await Cache.getItem(CacheQuery.USER))?.email,
      });
      if (data) {
        const account = data.find(({ AccountID }) => AccountID === params.accountId);
        account && setCurrentAccount(account);
      }
    }
  }, [accounts, currentAccount, params.accountId, user]);

  useEffect(() => {
    !accounts.length && fetchAccounts();
    fetchAccount();
  }, [accounts, fetchAccount, fetchAccounts]);

  return (
    <Provider value={{ accounts, currentAccount }}>{loading ? <Preloader variant="blank" /> : children}</Provider>
  );
};

const useAccounts = () => {
  return useContext(Context);
};

export { AccountsProvider, useAccounts };
